import type {
  DocumentKind,
  DocumentSide,
  EligibilityReasonCode,
  PromoRefusal,
  QuoteLineKind,
  RefusalCode,
  ReservationStatus,
  VehicleKind,
} from '@rental/domain'
import type { PublicErrorCode } from '../errors'

/**
 * Italian is the source language: English and German are typed against this
 * file, so a missing or extra key fails typecheck. The copy is a first draft:
 * the company reviews every language before launch (OPEN_QUESTIONS).
 */
export const it = {
  meta: {
    language: 'Italiano',
    htmlLang: 'it',
  },

  site: {
    name: 'Mazzella Rent',
    tagline: 'A Ischia dal 1965',
    skipToContent: 'Vai al contenuto',
    languageSwitcher: 'Lingua del sito',
    sampleBanner:
      'Dati di prova: veicoli, prezzi e disponibilità non sono reali. Le prenotazioni fatte qui non valgono.',
    footerAddress: 'Terminal Traghetti e Terminal Aliscafi, Ischia Porto',
    bookingTitle: 'Prenota auto, scooter ed e-bike a Ischia',
    bookingDescription:
      'Noleggio auto, scooter ed e-bike a Ischia: prezzi e disponibilità in tempo reale, ritiro al porto o consegna in hotel.',
  },

  common: {
    back: 'Indietro',
    continue: 'Continua',
    edit: 'Modifica',
    retry: 'Riprova',
    loading: 'Caricamento…',
    optional: 'facoltativo',
    total: 'Totale',
    select: 'Seleziona',
    yes: 'Sì',
    no: 'No',
    days: { one: '{count} giorno', other: '{count} giorni' },
    perDay: '{amount} al giorno',
    perRental: '{amount} a noleggio',
    steps: 'Passaggio {current} di {total}',
    /** The steps' short names, under each segment of the step bar (StepProgress). */
    stepNames: { search: 'Ricerca', results: 'Veicolo', options: 'Extra', details: 'Dati' },
    vehicle: 'Veicolo',
  },

  legs: {
    pickup: 'Ritiro',
    return: 'Riconsegna',
  },

  search: {
    title: 'Prenota il tuo veicolo',
    intro:
      'Auto, scooter ed e-bike a Ischia. Scegli dove e quando: ti mostriamo subito prezzi e disponibilità.',
    pickupPlace: 'Luogo di ritiro',
    returnPlace: 'Luogo di riconsegna',
    sameAsPickup: 'Stesso luogo del ritiro',
    officesGroup: 'I nostri uffici',
    deliveryGroup: 'Consegna',
    deliveryFee: '{name} (consegna {amount})',
    pickupDate: 'Data di ritiro',
    pickupTime: 'Ora di ritiro',
    returnDate: 'Data di riconsegna',
    returnTime: 'Ora di riconsegna',
    rentalDay: 'Un giorno di noleggio va dalle {time} alle {time} del giorno dopo.',
    closed: 'Chiuso in questa data',
    noTimesLeft: 'Per oggi non ci sono più orari',
    promoToggle: 'Hai un codice promozionale?',
    promoCode: 'Codice promozionale',
    submit: 'Cerca disponibilità',
    returnBeforePickup: 'La riconsegna deve essere dopo il ritiro.',
  },

  results: {
    title: 'Scegli il veicolo',
    period: '{from} → {to}',
    choose: 'Scegli',
    unavailable: 'Esaurito per queste date',
    notBookable: 'Non prenotabile per queste date',
    none: 'Nessun veicolo disponibile per queste date. Prova a cambiare date o luogo.',
    editSearch: 'Modifica ricerca',
    promoApplied: 'Codice {code} applicato.',
    promoNotApplied: 'Codice {code} non applicato: {reason}',
    promoBlocks:
      'Con questo codice non si può prenotare per questa ricerca: cambia date o luogo, oppure cerca senza codice.',
    searchWithoutCode: 'Cerca senza codice',
  },

  options: {
    title: 'Extra e coperture',
    extras: 'Extra',
    noExtras: 'Nessun extra disponibile per questo veicolo.',
    quantity: 'Quantità di {name}',
    coverages: 'Coperture',
    included: 'Inclusa',
    excess: 'Franchigia {amount}',
    summary: 'Il tuo noleggio',
    updating: 'Aggiornamento del prezzo…',
  },

  details: {
    title: 'I tuoi dati',
    driver: 'Conducente',
    driverNote: 'Può guidare solo chi firma il contratto di noleggio.',
    firstName: 'Nome',
    lastName: 'Cognome',
    email: 'E-mail',
    phone: 'Telefono',
    phoneHint: 'Con il prefisso internazionale, per esempio +39 333 123 4567.',
    birthDate: 'Data di nascita',
    licence: 'Patente di guida',
    licenceAccepted: 'Patenti accettate per questo veicolo: {categories}.',
    licenceYears: {
      one: 'La categoria deve essere stata ottenuta da almeno {count} anno.',
      other: 'La categoria deve essere stata ottenuta da almeno {count} anni.',
    },
    licenceNumber: 'Numero della patente',
    issuingCountry: 'Paese di rilascio',
    licenceCategory: 'Categoria',
    firstIssuedOn: 'Data in cui hai ottenuto la categoria',
    firstIssuedHint: 'È la data al punto 10 della patente europea.',
    expiresOn: 'Data di scadenza',
    hasIdp: 'Ho anche un permesso internazionale di guida (IDP)',
    notes: "Note per l'ufficio",
    termsBefore: 'Ho letto e accetto le',
    termsLink: 'condizioni di noleggio',
    termsVersion: 'Versione {version}',
    privacyBefore: 'Come trattiamo i tuoi dati:',
    privacyLink: 'informativa privacy',
    paymentTitle: 'Pagamento al ritiro',
    paymentReadyTitle: 'Pagamento',
    paymentReady:
      'Dopo la conferma puoi pagare subito online, su una pagina sicura di Stripe, oppure al ritiro in contanti o con carta.',
    paymentPending:
      "Il pagamento online non è ancora attivo: paghi all'ufficio al ritiro, in contanti o con carta.",
    deposit: 'Al ritiro serve un deposito cauzionale su carta di credito o di debito.',
    submit: 'Conferma la prenotazione',
    submitting: 'Invio in corso…',
    pendingNotice:
      'Non sappiamo se la richiesta precedente è arrivata. La ricontrolliamo prima di tutto: non verrà creata una seconda prenotazione.',
    checkPending: 'Controlla la richiesta precedente',
    priceChanged:
      'Il prezzo è cambiato: il nuovo totale è {amount}. Controlla e conferma di nuovo.',
  },

  done: {
    title: 'Prenotazione confermata',
    titleEnded: 'La tua prenotazione',
    code: 'Codice di prenotazione',
    codeHint: 'Conserva questo codice: ti servirà al ritiro.',
    status: 'Stato',
    payAtCounter: 'Paghi {amount} al ritiro, in contanti o con carta.',
    /** S5: a booking a bank disputed is settled at the counter only. */
    counterOnly: 'Paghi {amount} al nostro banco, in contanti o con carta.',
    checks: 'Al ritiro controlleremo:',
    documents: 'Documenti',
    documentsIntro:
      "Facoltativo: carica ora il documento d'identità e la patente, così il ritiro è più veloce.",
    documentKind: 'Documento',
    documentSide: 'Lato',
    file: 'Scegli il file',
    noFile: 'Nessun file scelto',
    fileHint: 'JPG, PNG o PDF, al massimo {size}.',
    upload: 'Carica',
    uploading: 'Caricamento…',
    uploaded: '{document}: caricato',
    newBooking: 'Nuova prenotazione',
    restoreFailed: 'Non riusciamo a ricaricare la tua prenotazione in questo momento.',
    payTitle: 'Pagamento',
    payIntro: 'Puoi pagare ora online, oppure al ritiro in contanti o con carta.',
    payOnline: 'Paga ora {amount}',
    payRedirecting: 'Ti portiamo alla pagina di pagamento…',
    payHint: 'Paghi su una pagina sicura di Stripe; poi torni qui.',
    paySample:
      'Pagamento di prova, nessun addebito reale. Usa la carta 4242 4242 4242 4242, una data futura e un CVC qualsiasi.',
    ended:
      'Questa prenotazione non è più attiva: non si paga online e non servono documenti. Per domande su pagamenti o rimborsi, contattaci.',
    paidTitle: 'Pagamento ricevuto',
    paidAmount: 'Pagato: {amount}.',
    balanceDue: 'Ancora da pagare: {amount}.',
    paymentConfirming: 'Stiamo confermando il pagamento…',
    paymentSlow:
      'La conferma tarda ad arrivare. Se hai pagato, comparirà qui a breve: non pagare una seconda volta.',
    paymentProcessing:
      'Il pagamento è in attesa di conferma dalla banca. Non serve fare altro: lo vedrai qui.',
    paymentCancelled: 'Pagamento non completato. Puoi riprovare oppure pagare al ritiro.',
    paymentFailed: 'Il pagamento non è andato a buon fine. Puoi riprovare oppure pagare al ritiro.',
    paymentNoBooking:
      'Sei tornato dalla pagina di pagamento, ma questa finestra non ha più la tua prenotazione. Se hai pagato, il pagamento è registrato e lo vedrai al ritiro.',
    payLinkInvalid:
      'Questo link di pagamento è scaduto o non è valido. Chiedine uno nuovo a Mazzella Rent, oppure paga al ritiro.',
  },

  validation: {
    required: 'Campo obbligatorio.',
    email: "Controlla l'indirizzo e-mail.",
    phone: 'Scrivi il numero con il prefisso internazionale, per esempio +39 333 123 4567.',
    date: 'Scegli una data valida.',
    pastDate: 'La data deve essere nel passato.',
    terms: 'Per prenotare devi accettare le condizioni di noleggio.',
    fileType: 'Formato non supportato: usa JPG, PNG o PDF.',
    fileSize: 'Il file è troppo grande: al massimo {size}.',
  },

  vehicleKinds: {
    car: 'Auto',
    van: 'Furgone',
    minivan: 'Monovolume',
    scooter: 'Scooter',
    motorcycle: 'Moto',
    ebike: 'E-bike',
  } satisfies Record<VehicleKind, string>,

  quoteLines: {
    rental: 'Noleggio',
    surcharge: 'Supplemento',
    discount: 'Sconto {code}',
    delivery: 'Consegna: {name}',
    collection: 'Ritiro a fine noleggio: {name}',
    one_way: 'Riconsegna in un altro luogo',
    extra: '{name}',
    coverage: '{name}',
  } satisfies Record<QuoteLineKind, string>,

  youngDriver: 'Supplemento giovane conducente',

  refusals: {
    invalid_period: 'La riconsegna deve essere dopo il ritiro.',
    in_the_past: 'Il ritiro è già passato: scegli una data futura.',
    category_unavailable: 'Questo veicolo non si può prenotare online.',
    season_not_configured: 'Non abbiamo ancora i prezzi dal {from} al {to}.',
    rate_not_configured: 'Non abbiamo ancora un prezzo per questa durata.',
    day_price_not_configured: 'Non abbiamo ancora i prezzi dal {from} al {to}.',
    stop_sale: 'Dal {from} al {to} non prenotiamo online: chiamaci o scrivici.',
    min_stay: 'Per queste date il noleggio minimo è di {minDays} giorni.',
    place_unknown: 'Questo luogo non è più disponibile.',
    outside_opening_hours: "{leg} alle {time}: l'ufficio {location} è chiuso a quell'ora.",
    delivery_blackout: '{leg} · {zone}: non disponibile in questo periodo.',
    delivery_lead_time: '{leg} · {zone}: serve un preavviso di almeno {minutes} minuti.',
    delivery_cutoff: '{leg} · {zone}: va prenotata entro le {cutoff} del giorno prima.',
    delivery_fee_not_configured: '{leg} · {zone}: non prenotabile online.',
    one_way_fee_not_configured: 'La riconsegna in un altro luogo non è disponibile online.',
    extra_unknown: 'Un extra scelto non è più disponibile.',
    extra_not_for_category: '{extra} non è disponibile per questo veicolo.',
    extra_quantity: '{extra}: al massimo {max} per prenotazione.',
    extra_out_of_stock: '{extra}: ne restano {left} per queste date.',
    coverage_unknown: 'Una copertura scelta non è più disponibile.',
    driver_too_young: 'Per questo veicolo il conducente deve avere almeno {minAge} anni.',
    young_driver_surcharge_not_configured:
      'Per un conducente della tua età questo veicolo si prenota solo in ufficio.',
  } satisfies Record<RefusalCode, string>,

  promoRefusals: {
    unknown: 'il codice non esiste.',
    inactive: 'il codice non è attivo.',
    not_yet_valid: 'il codice non è ancora valido.',
    expired: 'il codice è scaduto.',
    exhausted: 'il codice è esaurito.',
    blackout: 'il codice non vale in queste date.',
    location: 'il codice vale solo per alcuni luoghi di ritiro e riconsegna.',
  } satisfies Record<PromoRefusal, string>,

  eligibility: {
    rules_not_configured: 'i requisiti del conducente per questo veicolo.',
    birth_date_missing: 'la tua data di nascita.',
    too_young: "l'età minima: servono almeno {minAge} anni.",
    licence_rules_not_configured: 'la patente richiesta per questo veicolo.',
    licence_missing: 'la tua patente di guida.',
    licence_expired: 'la scadenza della patente ({expiresOn}).',
    licence_category: 'la categoria della patente: accettiamo {accepted}.',
    licence_first_issue_unknown: 'da quanto tempo hai la patente.',
    licence_too_recent: 'da quanto tempo hai la patente: servono almeno {minYears} anni.',
    idp_required:
      'il permesso internazionale di guida, richiesto per una patente di questo paese: {country}.',
    manual_check: 'la patente, come facciamo per tutti i noleggi di questo veicolo.',
  } satisfies Record<EligibilityReasonCode, string>,

  documentKinds: {
    id_card: "Carta d'identità",
    passport: 'Passaporto',
    driving_licence: 'Patente di guida',
    other: 'Altro documento',
  } satisfies Record<DocumentKind, string>,

  documentSides: {
    front: 'Fronte',
    back: 'Retro',
    single: 'Pagina unica',
  } satisfies Record<DocumentSide, string>,

  reservationStatuses: {
    draft: 'In attesa',
    confirmed: 'Confermata',
    checked_out: 'In corso',
    returned: 'Riconsegnata',
    closed: 'Chiusa',
    cancelled: 'Annullata',
    no_show: 'Mancato ritiro',
  } satisfies Record<ReservationStatus, string>,

  payment: {
    /** The line Stripe's page shows. */
    item: 'Noleggio Mazzella Rent, prenotazione {code}',
    itemSample: 'Pagamento di prova: prenotazione {code}',
  },

  emails: {
    greeting: 'Ciao {name},',
    signature: 'Mazzella Rent, Ischia dal 1965',
    sample: 'Prenotazione di prova: nessun veicolo è riservato davvero.',
    automatic: 'Questa e-mail è inviata automaticamente: non rispondere a questo indirizzo.',
    call: 'Per cambiare qualcosa chiamaci al {phone}.',
    pickupReminder: {
      subject: 'Promemoria: ritiro {when} · prenotazione {code}',
      intro: 'ti ricordiamo il ritiro della tua prenotazione.',
      ferry: 'Traghetto',
    },
    returnReminder: {
      subject: 'Promemoria: riconsegna {when} · noleggio {code}',
      intro: 'ti ricordiamo la riconsegna del veicolo.',
      due: 'Restano da pagare {amount}: li paghi alla riconsegna, in contanti o con carta.',
    },
    questions: 'Per qualsiasi domanda chiamaci al {phone}.',
    /** Q-97 (S7): a cancellation, facts only: no clause is quoted. */
    bookingCancelled: {
      subject: 'Prenotazione {code} annullata',
      intro: 'la tua prenotazione è stata annullata.',
      paid: 'Pagato',
      fee: 'Penale di annullamento',
      returned: 'Già restituito',
      toReturn: 'Da restituire',
      refundFollows: 'Ti scriveremo quando il rimborso sarà stato inviato.',
    },
    /** W2-S S4: a website booking's hold let go, its online share not paid in time. */
    holdReleased: {
      subject: 'Prenotazione {code} non completata',
      intro:
        'il pagamento online della tua prenotazione non è stato completato in tempo, quindi il veicolo non è più riservato per te e nulla ti è stato addebitato.',
      bookAgain: 'Puoi prenotare di nuovo qui: {url}',
    },
    /** Q-97 (S7): a refund sent; "sent", never "received": a card refund can still fail. */
    refundSent: {
      subject: 'Prenotazione {code}: rimborso di {amount}',
      intro: 'ti abbiamo inviato un rimborso.',
      amount: 'Importo',
      method: 'Modalità',
      methods: {
        stripe: 'Sulla carta usata per il pagamento',
        card_terminal: 'Sulla carta, al nostro terminale',
        bank_transfer: 'Con bonifico',
        cash: 'In contanti, al banco',
      },
      timing: {
        stripe: 'Di solito compare sull’estratto conto entro 5–10 giorni lavorativi.',
        card_terminal: 'I tempi di accredito dipendono dalla banca della carta.',
        bank_transfer: 'I tempi di accredito dipendono dalle banche.',
        cash: 'Ti è stato consegnato al banco.',
      },
    },
    paymentLink: {
      subject: 'Prenotazione {code}: link per il pagamento',
      intro: 'ecco il link per pagare online la tua prenotazione.',
      introFineFee:
        'ecco il link per pagare online quanto resta del tuo noleggio: la gestione di una multa ricevuta per il noleggio.',
      balance: 'Da pagare',
      pay: 'Paga in modo sicuro con carta sulla pagina di Stripe: {url}',
      expires: 'Il link vale fino al {date}.',
      ignore:
        'Se hai già pagato, o se non hai prenotato tu, ignora questa e-mail: il link mostra solo l’importo da pagare.',
    },
    contractSigned: {
      subject: 'Noleggio {code}: il tuo contratto firmato',
      intro: 'in allegato trovi il contratto di noleggio che hai firmato al banco.',
      language: 'Il contratto è in italiano.',
      keep: 'Conservalo per tutta la durata del noleggio.',
    },
    bookingConfirmed: {
      subject: 'Prenotazione {code} confermata',
      intro: 'la tua prenotazione è confermata.',
      code: 'Codice di prenotazione',
      keepCode: 'Conserva questo codice: ti servirà al ritiro.',
      pickup: 'Ritiro',
      return: 'Riconsegna',
      vehicle: 'Veicolo',
      total: 'Totale',
      payAtCounter: 'Paghi {amount} al ritiro, in contanti o con carta.',
      payUnlessPaidOnline:
        'Se non hai già pagato online, paghi {amount} al ritiro, in contanti o con carta.',
      partlyPaid:
        'Pagato finora: {paid}. Il resto, {balance}, lo paghi al ritiro, in contanti o con carta.',
      paid: 'Pagamento ricevuto: {amount}. Grazie!',
      bringLicence: 'Al ritiro porta un documento d’identità e la patente di guida.',
      bringId: 'Al ritiro porta un documento d’identità.',
    },
    rentalReturned: {
      subject: 'Noleggio {code}: riepilogo',
      intro: 'grazie per aver noleggiato con noi. Ecco il riepilogo del noleggio.',
      code: 'Codice di prenotazione',
      vehicle: 'Veicolo',
      returned: 'Riconsegna',
      km: 'Chilometri percorsi',
      rental: 'Noleggio',
      paid: 'Pagato',
      balance: 'Da pagare',
      settled: 'Tutto pagato. Grazie e a presto!',
      due: 'Restano da pagare {amount}.',
      notReceipt: 'Questo riepilogo non è un documento fiscale.',
      chargeKinds: {
        fuel: 'Carburante',
        late: 'Ritardo',
        cleaning: 'Pulizia',
        damage: 'Danni',
        other: 'Altro',
        fine_fee: 'Gestione di una multa ricevuta per il noleggio',
        excess_km: 'Chilometri eccedenti',
      },
    },
  },

  /**
   * CTR-2: the customer's signing screen on the counter iPad, in the
   * booking's language. The contract itself is Italian for everyone (Fady,
   * 8 Oct 2026); only these words around it are translated.
   */
  signing: {
    title: 'Il tuo contratto di noleggio',
    intro: 'Leggi il contratto e firma nei due riquadri in fondo.',
    language:
      'Il contratto e le condizioni sono in italiano. Se qualcosa non è chiaro, chiedi al personale.',
    draft: 'BOZZA: condizioni di prova, senza valore legale.',
    signMain: 'Firma qui per accettare il contratto e le condizioni',
    signApproval: 'Firma qui per approvare specificamente i punti elencati sopra',
    pad: 'Riquadro per la firma: firma con il dito o con la penna.',
    padEmpty: 'Nessuna firma',
    padSigned: 'Firma presente',
    clear: 'Cancella la firma',
    submit: 'Firma il contratto',
    submitHint: 'Firma in entrambi i riquadri per continuare.',
    signedTitle: 'Grazie, il contratto è firmato',
    signedBody: 'Restituisci il dispositivo al personale.',
    signedEmail: 'Riceverai una copia del contratto via e-mail.',
    done: 'Restituisci il dispositivo',
    cancel: 'Non firmo ora',
    expires: 'Questa schermata si chiude alle {time}.',
    /** C7: the contract could not be read; said in it, en and de, the booking's language unknown. */
    unavailable: 'Il contratto non si apre su questo dispositivo. Restituiscilo al personale.',
  },

  /** CTR-1: the words of the contract document itself, on the screen and in the PDF. */
  contract: {
    title: 'Contratto di noleggio',
    booking: 'Prenotazione {code}',
    draft: 'BOZZA',
    draftNotice: 'Bozza: condizioni di prova, senza valore legale.',
    sections: {
      company: 'Locatore',
      customer: 'Cliente',
      licence: 'Patente di guida',
      secondDriver: 'Secondo conducente',
      vehicle: 'Veicolo',
      period: 'Periodo di noleggio',
      prices: 'Prezzi',
      cover: 'Copertura',
      deposit: 'Deposito cauzionale',
      fees: 'Penali e costi previsti',
      approval: 'Approvazione specifica (artt. 1341 e 1342 c.c.)',
      signatures: 'Firme',
    },
    fields: {
      name: 'Nome',
      address: 'Indirizzo',
      vatNumber: 'Partita IVA',
      birthDate: 'Data di nascita',
      birthPlace: 'Luogo di nascita',
      nationality: 'Nazionalità',
      email: 'E-mail',
      phone: 'Telefono',
      licenceNumber: 'Numero',
      issuingCountry: 'Paese di rilascio',
      expiresOn: 'Scadenza',
      categories: 'Categorie',
      plate: 'Targa',
      model: 'Modello',
      category: 'Categoria',
      from: 'Ritiro',
      to: 'Riconsegna',
      total: 'Totale',
      excess: 'Franchigia',
    },
    notConfigured: 'da configurare',
    noLicence: 'Nessuna patente registrata',
    noCover: 'Solo responsabilità civile (R.C.A.)',
    noExcess: 'nessuna',
    depositPending: 'Da definire: importo e modalità non sono ancora decisi.',
    depositAmount: '{amount}, modalità da definire',
    adjustment: 'Variazione concordata al banco',
    secondDriverFee: 'Secondo conducente',
    fees: {
      fuel_refill: 'Mancato rabbocco del carburante',
      one_way: 'Riconsegna in un altro luogo',
      fine_admin: 'Gestione di una multa ricevuta per il noleggio',
    },
    signatureMain: 'Firma del cliente',
    signatureApproval: 'Firma per approvazione specifica',
    signedAt: 'Firmato il {date}',
    fingerprint: 'Impronta SHA-256 del contratto: {hash}',
    termsFingerprint: 'Condizioni {version}, impronta SHA-256: {hash}',
  },

  errors: {
    network: 'Non riusciamo a raggiungere il servizio. Controlla la connessione e riprova.',
    fallback: 'Qualcosa non ha funzionato. Riprova tra poco.',
    codes: {
      validation_failed: 'Alcuni dati non sono validi. Controlla i campi e riprova.',
      bad_request: 'La richiesta non è valida. Ricarica la pagina e riprova.',
      not_found: 'Non abbiamo trovato quello che cerchi.',
      internal_error: 'Qualcosa non ha funzionato da parte nostra. Riprova tra poco.',
      rate_limited: 'Troppe richieste in poco tempo. Riprova tra {retryAfterSeconds} secondi.',
      app_version_required: 'Ricarica la pagina e riprova.',
      app_version_unsupported: 'È disponibile una nuova versione. Ricarica la pagina.',
      not_configured: 'La prenotazione online non è ancora configurata per queste date.',
      category_not_found: 'Questo veicolo non è più disponibile.',
      category_inactive: 'Questo veicolo non è più disponibile.',
      not_available: 'Ci dispiace: il veicolo non è più disponibile per queste date.',
      quote_refused: 'Non possiamo calcolare il prezzo per questa scelta.',
      promo_refused: 'Il codice promozionale non vale per questa prenotazione.',
      not_eligible: 'Il conducente non ha i requisiti per questo veicolo.',
      price_changed: 'Il prezzo è cambiato. Controlla il nuovo totale e conferma di nuovo.',
      terms_changed: 'Le condizioni di noleggio sono cambiate. Leggile e accettale di nuovo.',
      terms_draft:
        'Non è possibile firmare né prenotare ora: le condizioni di noleggio non sono disponibili. Rivolgiti a noi.',
      idempotency_key_required: 'Ricarica la pagina e riprova.',
      idempotency_key_invalid: 'Ricarica la pagina e riprova.',
      idempotency_key_reused:
        'Questa richiesta è già stata inviata con dati diversi. Ricarica la pagina.',
      idempotency_key_expired:
        'Questa richiesta è troppo vecchia per essere ripetuta. Ricarica la pagina e controlla prima di riprovare.',
      booking_not_found: 'Nessuna prenotazione corrisponde a questi dati.',
      booking_token_invalid: 'Il link è scaduto. Cerca di nuovo la prenotazione.',
      upload_link_invalid: 'Il link di caricamento è scaduto. Riprova.',
      already_uploaded: 'Questo file è già stato caricato.',
      booking_closed: 'Questa prenotazione non è più attiva: non si aggiungono documenti.',
      file_too_large: 'Il file è troppo grande.',
      unsupported_content_type: 'Formato non supportato: usa JPG, PNG o PDF.',
      wrong_content_type: 'Il file non corrisponde al formato dichiarato. Riprova.',
      content_mismatch: 'Il contenuto del file non corrisponde al suo formato.',
      too_many_documents: 'Hai già caricato il numero massimo di documenti.',
      vehicle_not_in_category: 'Questo veicolo non è più disponibile.',
      hold_required: 'Qualcosa non ha funzionato da parte nostra. Riprova tra poco.',
      illegal_transition: 'Questa prenotazione non si può più modificare.',
      version_conflict: 'I dati sono cambiati nel frattempo. Ricarica la pagina.',
      invalid_period: 'La riconsegna deve essere dopo il ritiro.',
      invalid_date: 'Scegli una data valida.',
      invalid_time: 'Scegli un orario valido.',
      invalid_amount: 'Qualcosa non ha funzionato da parte nostra. Riprova tra poco.',
      invalid_quantity: 'Controlla le quantità scelte.',
      invalid_rate: 'Qualcosa non ha funzionato da parte nostra. Riprova tra poco.',
      nothing_to_pay: 'Per questa prenotazione non c’è nulla da pagare.',
      booking_not_payable: 'Questa prenotazione non si può pagare online.',
      booking_disputed: 'Questa prenotazione si salda al nostro banco, in contanti o con carta.',
      payment_processing:
        'Un pagamento per questa prenotazione è in fase di conferma. Attendi qualche minuto prima di riprovare.',
      online_payment_pending: 'Il pagamento online non è ancora attivo: paghi al ritiro.',
      hold_ended:
        'Il tempo per pagare online è scaduto e il veicolo non è più riservato: puoi prenotare di nuovo.',
      contact_unavailable:
        'Il modulo non può inviare messaggi in questo momento: chiamaci o scrivici su WhatsApp.',
      contact_check_failed:
        'Il controllo anti-spam non è riuscito: riprova, o chiamaci o scrivici su WhatsApp.',
      payment_provider_error:
        'Il servizio di pagamento non risponde. Non è stato addebitato nulla: riprova tra poco.',
      signing_required:
        'Questa schermata non è più attiva. Restituisci il dispositivo al personale.',
      contract_changed:
        'Il contratto è cambiato nel frattempo. Restituisci il dispositivo al personale, che te lo mostrerà di nuovo.',
      signature_invalid: 'La firma non si legge. Cancellala e firma di nuovo.',
    } satisfies Record<PublicErrorCode, string>,
  },
  /**
   * S8: the README of a customer's data export (access, art. 15 GDPR;
   * portability, art. 20), in their language. The file and key names it
   * quotes stay as data.json writes them.
   */
  dataExport: {
    fileName: 'LEGGIMI.txt',
    title: 'I tuoi dati presso {company}',
    intro:
      'Questo archivio contiene i dati personali che {company} conserva su di te, preparati il {date} per la tua richiesta del {requestedOn} ({purpose}).',
    purposes: {
      access: 'accesso ai dati, art. 15 GDPR',
      portability: 'portabilità dei dati, art. 20 GDPR',
    },
    contentsTitle: 'Cosa contiene',
    contents: [
      'data.json: i tuoi dati, in un formato che un programma sa leggere (JSON, UTF-8).',
      'files/contracts: i contratti che hai firmato, in PDF.',
      'files/fine-letters: le lettere con cui abbiamo indicato all’autorità chi guidava, quando una multa è arrivata per un tuo noleggio.',
      'files/documents: le foto dei tuoi documenti, se le hai chieste e se le conserviamo ancora.',
    ],
    readingTitle: 'Come leggerlo',
    reading: [
      'In data.json: customer (i tuoi dati), licences (le patenti), bookings (le prenotazioni, ciascuna con noleggio, contratti, pagamenti, rimborsi, cauzioni, documenti fiscali, pagine di pagamento, multe e consegne: dove ti abbiamo incontrato, una nota, il traghetto con cui sei arrivato), identities (i dati raccolti alla consegna), secondDriver (le volte in cui sei stato indicato come secondo conducente sulla prenotazione di un altro: le date, il supplemento, l’esito dei requisiti), cargosReports (le comunicazioni alla Polizia di Stato, CaRGOS), documents (i documenti caricati), legacyRecords (i dati del sistema precedente).',
      'Gli importi sono in centesimi di euro: 12345 vuol dire 123,45 €.',
      'Date e orari seguono lo standard ISO 8601; gli orari sono in UTC (l’ora italiana è un’ora avanti d’inverno, due d’estate).',
      'Un file elencato con "path": null non è nell’archivio, e "state" dice perché: erased (cancellato), not_requested (non chiesto), never_uploaded (mai caricato), unreadable (non è stato possibile leggerlo).',
    ],
    leftOutTitle: 'Cosa non contiene',
    leftOut: [
      'I tratti della tua firma (tempi e pressione della penna), che permetterebbero di imitarla: il contratto in PDF mostra la firma come l’hai apposta.',
      'Le foto e le note sul veicolo prese alla consegna e al rientro: riguardano il veicolo. I danni addebitati a te sono elencati con i loro addebiti.',
      'I nomi del personale e i registri interni dell’azienda.',
      'Quello che Stripe conserva sui pagamenti con carta: puoi chiederlo a Stripe.',
    ],
    contact: 'Per qualsiasi domanda su questi dati rivolgiti a {company}.',
  },
} as const
